/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
	Card,
	Col,
	Form,
	Row,
	Spin,
	Switch,
	Select,
	message,
	Alert,
	Divider,
	Button,
	Tooltip,
	Space,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
	SaveOutlined,
	ReloadOutlined,
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	UndoOutlined,
} from '@ant-design/icons';
import {
	getDriverApprovalSettings,
	updateDriverApprovalSettings,
} from '../../redux/driverApprovalSettingsSlice';
import { DRIVER_APPROVAL_MESSAGES } from '../../Utils/Constants/messages';

const { Option } = Select;

const DriverApprovalSettings = () => {
	const [form] = Form.useForm();
	const [makingApiCall, setMakingApiCall] = useState(false);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [lastSavedAt, setLastSavedAt] = useState(null);
	const dispatch = useDispatch();

	const { driverApprovalSettings } = useSelector((state) => state.driverApprovalSettings);
	const { loading, data } = driverApprovalSettings;
	const [isErrorDisplayed, setErrorDisplayed] = useState(false);

	const [formValues, setFormValues] = useState({
		driverApprovalEnabled: true, // Always true
		approvalRequired: true, // Default to manual approval
		autoApprovalEnabled: false, // Auto approval disabled by default
		autoApprovalAction: 'APPROVED',
	});

	const fetchSettings = async () => {
		setMakingApiCall(true);

		dispatch(getDriverApprovalSettings())
			.unwrap()
			.then(() => {
				setMakingApiCall(false);
			})
			.catch((err) => {
				setMakingApiCall(false);
				setErrorDisplayed(true);
				let errorMessage
				if (err.message === 'DRIVER_APPROVAL_SETTING_NOT_FOUND') {
					errorMessage = DRIVER_APPROVAL_MESSAGES.FAILED_TO_LOAD
					setHasUnsavedChanges(true);
				} else {
					errorMessage = DRIVER_APPROVAL_MESSAGES.SERVER_ERROR;
				}


				message.error(errorMessage)
			});
	};

	const handleFormChange = (changedValues, allValues) => {
		const updatedValues = { ...allValues };
		setErrorDisplayed(false);
		// Implement mutual exclusivity between manual approval and auto approval
		// At least one must always be enabled
		if ('approvalRequired' in changedValues) {
			if (changedValues.approvalRequired) {
				// If manual approval is enabled, disable auto approval
				updatedValues.autoApprovalEnabled = false;
			} else {
				// If manual approval is disabled, enable auto approval
				updatedValues.autoApprovalEnabled = true;
			}
		}

		if ('autoApprovalEnabled' in changedValues) {
			if (changedValues.autoApprovalEnabled) {
				// If auto approval is enabled, disable manual approval
				updatedValues.approvalRequired = false;
			} else {
				// If auto approval is disabled, enable manual approval
				updatedValues.approvalRequired = true;
			}
		}

		// Update form with the mutually exclusive values
		if (JSON.stringify(updatedValues) !== JSON.stringify(allValues)) {
			form.setFieldsValue(updatedValues);
		}

		setFormValues(updatedValues);
		setHasUnsavedChanges(true);
	};

	const handleSave = async (values = formValues) => {
		setErrorDisplayed(false);
		setMakingApiCall(true);
		try {
			await dispatch(updateDriverApprovalSettings(values)).unwrap();
			message.success(DRIVER_APPROVAL_MESSAGES.SETTINGS_UPDATED_SUCCESS);
			setHasUnsavedChanges(false);
			setLastSavedAt(new Date());
		} catch (err) {
			message.error(DRIVER_APPROVAL_MESSAGES.FAILED_TO_UPDATE);
		} finally {
			setMakingApiCall(false);
		}
	};

	const handleReset = () => {
		if (data) {
			const settings = {
				driverApprovalEnabled: true, // Always true - not configurable
				approvalRequired: data.approvalRequired,
				autoApprovalEnabled: data.autoApprovalEnabled,
				autoApprovalAction: data.autoApprovalAction || 'APPROVED',
			};

			// Ensure mutual exclusivity - if both are somehow true, prioritize manual approval
			if (settings.approvalRequired && settings.autoApprovalEnabled) {
				settings.autoApprovalEnabled = false;
			}

			setFormValues(settings);
			form.setFieldsValue(settings);
			setHasUnsavedChanges(false);
		}
	};

	useEffect(() => {
		fetchSettings();
	}, []);

	useEffect(() => {
		if (data) {
			const settings = {
				driverApprovalEnabled: data.driverApprovalEnabled ?? true, // Default to true if not specified
				approvalRequired: data.approvalRequired ?? true,
				autoApprovalEnabled: data.autoApprovalEnabled ?? false,
				autoApprovalAction: data.autoApprovalAction || 'APPROVED',
			};
			setFormValues(settings);
			form.setFieldsValue(settings);
		}
	}, [data, form]);

	const getApprovalFlowDescription = () => {
		if (formValues.approvalRequired) {
			return DRIVER_APPROVAL_MESSAGES.MANUAL_APPROVAL_FLOW;
		}
		if (formValues.autoApprovalEnabled) {
			return DRIVER_APPROVAL_MESSAGES.AUTO_APPROVAL_FLOW(formValues.autoApprovalAction);
		}
		return DRIVER_APPROVAL_MESSAGES.NO_AUTO_APPROVAL_FLOW;
	};

	const getApprovalFlowType = () => {
		if (formValues.approvalRequired) return 'info';
		if (formValues.autoApprovalEnabled && formValues.autoApprovalAction === 'APPROVED')
			return 'success';
		if (formValues.autoApprovalEnabled && formValues.autoApprovalAction === 'DENIED')
			return 'warning';
		return 'info';
	};

	return (
		<Spin spinning={loading || makingApiCall}>
			<Card
				title={DRIVER_APPROVAL_MESSAGES.CARD_TITLE}
				bordered={false}
				style={{ marginBottom: 16 }}
				extra={
					<Space>
						{hasUnsavedChanges && (
							<Tooltip title={DRIVER_APPROVAL_MESSAGES.UNSAVED_CHANGES}>
								<ExclamationCircleOutlined style={{ color: '#faad14' }} />
							</Tooltip>
						)}
						{lastSavedAt && !hasUnsavedChanges && (
							<Tooltip
								title={DRIVER_APPROVAL_MESSAGES.LAST_SAVED(
									lastSavedAt.toLocaleTimeString(),
								)}>
								<CheckCircleOutlined style={{ color: '#52c41a' }} />
							</Tooltip>
						)}
						<Button
							type='default'
							icon={<ReloadOutlined />}
							onClick={fetchSettings}
							loading={loading}
							disabled={makingApiCall}>
							{DRIVER_APPROVAL_MESSAGES.REFRESH}
						</Button>
						<Button
							type='default'
							icon={<UndoOutlined />}
							onClick={handleReset}
							disabled={!hasUnsavedChanges}>
							{DRIVER_APPROVAL_MESSAGES.RESET}
						</Button>
						<Button
							type='primary'
							icon={<SaveOutlined />}
							onClick={() => handleSave()}
							loading={makingApiCall}
							disabled={!hasUnsavedChanges}>
							{DRIVER_APPROVAL_MESSAGES.SAVE_CHANGES}
						</Button>
					</Space>
				}>
				{isErrorDisplayed && (
					<Alert
						message={DRIVER_APPROVAL_MESSAGES.ERROR_TITLE}
						description={DRIVER_APPROVAL_MESSAGES.FAILED_TO_LOAD}
						type='error'
						closable
						style={{ marginBottom: 16 }}
					/>
				)}

				<Alert
					message={DRIVER_APPROVAL_MESSAGES.CURRENT_APPROVAL_FLOW}
					description={getApprovalFlowDescription()}
					type={getApprovalFlowType()}
					showIcon
					style={{ marginBottom: 24 }}
				/>

				<Form
					form={form}
					layout='vertical'
					onValuesChange={handleFormChange}
					initialValues={formValues}>
					<Row gutter={[24, 16]}>
						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name='driverApprovalEnabled'
								label={DRIVER_APPROVAL_MESSAGES.DRIVER_APPROVAL_FEATURE_LABEL}
								valuePropName='checked'
								tooltip={DRIVER_APPROVAL_MESSAGES.DRIVER_APPROVAL_FEATURE_TOOLTIP}>
								<Switch
									checkedChildren={DRIVER_APPROVAL_MESSAGES.ENABLED}
									unCheckedChildren={DRIVER_APPROVAL_MESSAGES.DISABLED}
									disabled
									checked
								/>
							</Form.Item>
						</Col>

						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name='approvalRequired'
								label={DRIVER_APPROVAL_MESSAGES.MANUAL_APPROVAL_LABEL}
								valuePropName='checked'
								tooltip={DRIVER_APPROVAL_MESSAGES.MANUAL_APPROVAL_TOOLTIP}>
								<Switch
									checkedChildren={DRIVER_APPROVAL_MESSAGES.REQUIRED}
									unCheckedChildren={DRIVER_APPROVAL_MESSAGES.NOT_REQUIRED}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name='autoApprovalEnabled'
								label={DRIVER_APPROVAL_MESSAGES.AUTO_APPROVAL_LABEL}
								valuePropName='checked'
								tooltip={DRIVER_APPROVAL_MESSAGES.AUTO_APPROVAL_TOOLTIP}>
								<Switch
									checkedChildren={DRIVER_APPROVAL_MESSAGES.ENABLED}
									unCheckedChildren={DRIVER_APPROVAL_MESSAGES.DISABLED}
									disabled={formValues.approvalRequired}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name='autoApprovalAction'
								label={DRIVER_APPROVAL_MESSAGES.AUTO_APPROVAL_ACTION_LABEL}
								tooltip={DRIVER_APPROVAL_MESSAGES.AUTO_APPROVAL_ACTION_TOOLTIP}>
								<Select disabled={formValues.approvalRequired}>
									<Option value='APPROVED'>
										<span style={{ color: '#52c41a' }}>
											{DRIVER_APPROVAL_MESSAGES.AUTO_APPROVE}
										</span>
									</Option>
									<Option value='DENIED'>
										<span style={{ color: '#ff4d4f' }}>
											{DRIVER_APPROVAL_MESSAGES.AUTO_DENY}
										</span>
									</Option>
								</Select>
							</Form.Item>
						</Col>
					</Row>

					<Divider />

					<Row gutter={16}>
						<Col span={24}>
							<h4>{DRIVER_APPROVAL_MESSAGES.GUIDELINES_TITLE}</h4>
							<ul style={{ paddingLeft: 20, color: '#666' }}>
								<li>
									<strong>
										{DRIVER_APPROVAL_MESSAGES.DRIVER_APPROVAL_FEATURE_LABEL}:
									</strong>{' '}
									{DRIVER_APPROVAL_MESSAGES.GUIDELINE_DRIVER_APPROVAL}
								</li>
								<li>
									<strong>
										{DRIVER_APPROVAL_MESSAGES.MANUAL_APPROVAL_LABEL}:
									</strong>{' '}
									{DRIVER_APPROVAL_MESSAGES.GUIDELINE_MANUAL_APPROVAL}
								</li>
								<li>
									<strong>{DRIVER_APPROVAL_MESSAGES.AUTO_APPROVAL_LABEL}:</strong>{' '}
									{DRIVER_APPROVAL_MESSAGES.GUIDELINE_AUTO_APPROVAL}
								</li>
								<li>
									<strong>
										{DRIVER_APPROVAL_MESSAGES.AUTO_APPROVAL_ACTION_LABEL}:
									</strong>{' '}
									{DRIVER_APPROVAL_MESSAGES.GUIDELINE_AUTO_APPROVAL_ACTION}
								</li>
							</ul>
						</Col>
					</Row>
				</Form>
			</Card>
		</Spin>
	);
};

export default DriverApprovalSettings;
